import { SYMPTOM_SHORT_NAMES } from '../device-checklists/checklists';
import { OUTCOME_LABELS } from '../engine/types';

import type { ICriticalReadingData } from '../types';

/**
 * Blurb model (per product): each answered question contributes its own descriptive
 * sentence; the note is those sentences concatenated (one per line). Not a label:value
 * list and not a single summary sentence. Signs & Symptoms are condensed to short
 * symptom names rather than the full question text.
 */

export const buildMainBlurb = (
  data: ICriticalReadingData,
  ctx: { noLiveCall: boolean },
): string => {
  const lines: string[] = [];
  const add = (s: string) => lines.push(s);

  // #1 Reading Status Check
  if (data.readingStatus === 'still-critical') {
    // A filled reason means Already Recovered was pre-selected and the MA overrode it.
    if (data.readingStatusReason?.trim()) {
      add(
        'Reading Status Check: System flags already recovered — MA records "Still Critical/At-Risk", and proceeds to #2 Contact Attempt.',
      );
      add(
        `Reason for changing Already Recovered to Still critical/at-risk: ${data.readingStatusReason.trim()}.`,
      );
    } else {
      add(
        'Reading Status Check: System flags still critical/at-risk — pre-selected, MA records and proceeds to #2 Contact Attempt.',
      );
    }
  } else if (data.readingStatus === 'already-recovered') {
    add(
      'Reading Status Check: System flags already recovered — MA records "Already Recovered". System pre-selects Close, no escalation and routes to #6 for confirmation. No call is placed.',
    );
  }

  // #2 Contact Attempt (skipped entirely if already recovered)
  if (data.readingStatus !== 'already-recovered') {
    if (data.contact === 'reached') {
      add(
        'Contact Attempt: Reached the patient (primary or secondary number) — proceeded to #3 Consent Verification.',
      );
    } else if (data.contact === 'not-reached') {
      if (data.unableToReachRunbook === 'do-not-escalate') {
        add(
          'Contact Attempt: Not Reached (all available numbers attempted). Runbook specifies Do Not Escalate — system pre-selects Do Not Escalate.',
        );
      } else if (data.unableToReachRunbook === 'no-entry') {
        add(
          'Contact Attempt: Not Reached (all available numbers attempted). No runbook entry — system pre-selects Escalate to Clinic (default).',
        );
      } else {
        add('Contact Attempt: Not Reached (all available numbers attempted).');
      }
    }
  }

  // #3 Consent / Communication Authorization Verification
  if (data.consent === 'patient') {
    add('Consent Verification: Spoke with the patient.');
  } else if (data.consent === 'representative') {
    add(
      "Consent Verification: Spoke with a representative who manages the patient's healthcare communications.",
    );
    if (data.representativeName) {
      add(`Representative Name: ${data.representativeName}.`);
    }
    if (data.representativeRelationship) {
      add(`Relationship to Patient: ${data.representativeRelationship}.`);
    }
    if (data.representativeAttestation === 'yes') {
      add(
        'Attestation obtained: the representative confirmed they are authorized.',
      );
    } else if (data.representativeAttestation === 'no') {
      add('Attestation not obtained.');
    }
  } else if (data.consent === 'unable-to-verify') {
    add(
      'Consent Verification: Unable to verify identity/authorization — system pre-selects Escalated to Clinic (Failed Verification, No Clinical Discussion).',
    );
  }

  // #4 Reading Recheck
  const recheckSentence: Record<string, string> = {
    normal:
      'Reading Recheck: Repeat reading is Back to Normal (system-determined); proceeded to Signs and Symptoms.',
    'at-risk':
      'Reading Recheck: Repeat reading is At-Risk (system-determined); proceeded to Signs and Symptoms.',
    'still-critical':
      'Reading Recheck: Repeat reading is Still Critical (system-determined); proceeded to Signs and Symptoms.',
    'unable-to-recheck':
      'Reading Recheck: Unable to obtain a repeat reading (MA-selected); proceeded to Signs and Symptoms.',
  };
  if (data.recheck && recheckSentence[data.recheck]) {
    add(recheckSentence[data.recheck]);
  }

  // #5 Signs and Symptoms — condensed to short symptom names, comma-separated.
  const symptomEntries = Object.entries(data).filter(([k]) =>
    k.startsWith('sym_'),
  );
  const reported = symptomEntries
    .filter(([, v]) => v === 'yes')
    .map(([k]) => SYMPTOM_SHORT_NAMES[k] ?? k);
  const denied = symptomEntries
    .filter(([, v]) => v === 'no')
    .map(([k]) => SYMPTOM_SHORT_NAMES[k] ?? k);
  // "Other symptom" appends as its own trailing sentence after the symptom list.
  let otherReported = '';
  if (data.symptomsOther === 'yes') {
    otherReported = data.symptomsOtherText
      ? `Other symptom reported: ${data.symptomsOtherText}.`
      : 'Other symptom reported.';
  }
  const otherDenied =
    data.symptomsOther === 'no' ? 'No other symptoms reported.' : '';
  if (reported.length || otherReported) {
    const list = reported.length
      ? `Signs and Symptoms reported (answered Yes): ${reported.join(', ')}.`
      : '';
    add([list, otherReported].filter(Boolean).join(' '));
  }
  if (denied.length || otherDenied) {
    const list = denied.length
      ? `Signs and Symptoms denied (answered No): ${denied.join(', ')}.`
      : '';
    add([list, otherDenied].filter(Boolean).join(' '));
  }

  // Emergency Prompt — only when any #5 answer was Yes
  const anySymptomYes =
    symptomEntries.some(([, v]) => v === 'yes') || data.symptomsOther === 'yes';
  if (anySymptomYes) {
    add(
      'Emergency Symptom Prompt Triggered: Yes. System-Recommended Action: Warm Transfer to TH Nurse. MA Confirmation: Confirmed.',
    );
  }

  // #6 MA Intervention / Outcome
  if (data.outcome) {
    add(`MA Intervention / Outcome: ${OUTCOME_LABELS[data.outcome]}.`);
  }
  if (data.overrideReason?.trim()) {
    add(
      `Outcome changed from the system pre-selection. Reason: ${data.overrideReason.trim()}.`,
    );
  }
  if (data.outcome === 'follow-runbook' && data.runbookAction) {
    const action =
      data.runbookAction === 'other'
        ? data.runbookActionOther || 'Other'
        : {
            'warm-transfer': 'Warm Transfer/Callback to TH Nurse',
            'escalate-clinic': 'Escalate to Clinic',
            '911': '911',
          }[data.runbookAction];
    add(`Runbook action entered: ${action}.`);
  }
  if (data.outcome === 'escalate-clinic' && data.escalationMethod) {
    const method = {
      phone: 'Phone',
      sms: 'SMS',
      email: 'Email',
      'ehr-case': 'Create Case in EHR',
      'ccm-team': 'Route to CCM Team',
    }[data.escalationMethod];
    add(`Escalation method: ${method}.`);
  }
  if (data.outcome === 'warm-transfer' && data.warmTransferConfirmed) {
    add('Successfully warm transferred patient to Tellihealth nurse.');
  }
  if (data.outcome && data.outcome !== 'warm-transfer' && !ctx.noLiveCall) {
    add('ER Precautions script was read to the patient.');
  }

  return lines.join('\n');
};
